/**
 * VHAKACHA PRICING  (web app mirror)
 * ============================================================
 * Mirror of  provider app → src/lib/pricing.ts
 * Keep both files in sync — a traveler and a provider must never
 * see different numbers for the same booking.
 *
 * All money is handled in integer minor units (cents) internally so
 * repeated addition cannot drift, and returned as 2dp majors.
 * ============================================================
 */

export const PRICE_UNITS = [
  { value: 'PER_NIGHT', label: 'Per night', hint: 'Charged once per night, regardless of guest count.' },
  { value: 'PER_PERSON', label: 'Per person', hint: 'Charged once per paying guest, regardless of duration.' },
  { value: 'PER_PERSON_PER_NIGHT', label: 'Per person, per night', hint: 'Guests x nights. Standard for safari lodges.' },
  { value: 'PER_GROUP', label: 'Flat rate', hint: 'One fixed charge for the whole booking.' },
];

export const SUPPORTED_CURRENCIES = ['USD', 'ZWG', 'ZAR', 'GBP', 'EUR'];
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_COMMISSION_RATE = 0.1;

const DEFAULT_UNIT_BY_TYPE = {
  'Safari Lodges & Camps': 'PER_PERSON_PER_NIGHT',
  'Hotels & Lodges': 'PER_NIGHT',
  'Fishing Camps': 'PER_PERSON_PER_NIGHT',
  'Conservation Camps': 'PER_PERSON_PER_NIGHT',
  'Cruises & Boat Tours': 'PER_PERSON',
  'Things To Do': 'PER_PERSON',
  'Culture & Heritage': 'PER_PERSON',
  'National Parks & Gardens': 'PER_PERSON',
  Restaurants: 'PER_PERSON',
};

export function defaultPriceUnitFor(type) {
  return (type && DEFAULT_UNIT_BY_TYPE[type]) || 'PER_PERSON';
}

const toCents = (major) => Math.round(Number(major || 0) * 100);
const toMajor = (cents) => Math.round(cents) / 100;

/** Coerce a form value that may arrive as "3" rather than 3. */
export function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function normalizeTravelers(raw) {
  return {
    adults: Math.max(1, toCount(raw && raw.adults != null ? raw.adults : 1)),
    children: toCount(raw && raw.children),
    infants: toCount(raw && raw.infants),
  };
}

/** Whole nights between two dates; a same-day activity counts as 1. */
export function nightsBetween(start, end) {
  if (!start || !end) return 1;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 1;
  return Math.max(1, Math.round(ms / 86400000));
}

export function quantityFor(unit, nights, billableGuests) {
  const n = Math.max(1, nights);
  const g = Math.max(1, billableGuests);
  switch (unit) {
    case 'PER_NIGHT':
      return n;
    case 'PER_PERSON':
      return g;
    case 'PER_PERSON_PER_NIGHT':
      return g * n;
    case 'PER_GROUP':
      return 1;
    default:
      return g;
  }
}

/** The single place a booking's money is decided. */
export function computeBookingTotals(input) {
  const priceUnit = (input && input.priceUnit) || 'PER_PERSON';
  const currency = (input && input.currency) || DEFAULT_CURRENCY;
  const travelers = normalizeTravelers(input && input.travelers);
  const nights = Math.max(1, Math.floor((input && input.nights) || 1));

  const guests = travelers.adults + travelers.children + travelers.infants;
  const billableGuests = Math.max(1, travelers.adults + travelers.children);

  const rawRate = input && input.commissionRate;
  const commissionRate = Number.isFinite(rawRate) && rawRate >= 0 ? rawRate : DEFAULT_COMMISSION_RATE;

  const unitCost = Number(input && input.unitCost) || 0;
  const quantity = quantityFor(priceUnit, nights, billableGuests);

  const subtotalCents = toCents(unitCost) * quantity;
  const commissionCents = Math.round(subtotalCents * commissionRate);
  const netCents = subtotalCents - commissionCents;

  return {
    currency,
    unitCost,
    priceUnit,
    nights,
    guests,
    billableGuests,
    quantity,
    subtotal: toMajor(subtotalCents),
    commissionRate,
    commissionDeducted: toMajor(commissionCents),
    netMerchantPay: toMajor(netCents),
    totalPaid: toMajor(subtotalCents),
  };
}

export function formatMoney(amount, currency = DEFAULT_CURRENCY) {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    return `${currency} ${value.toFixed(2)}`;
  }
}

/** Short label for a listing card, e.g. "$200 per person, per night". */
export function formatUnitPrice(cost, currency = DEFAULT_CURRENCY, unit = 'PER_PERSON') {
  const found = PRICE_UNITS.find((u) => u.value === unit);
  return `${formatMoney(cost, currency)} ${(found ? found.label : 'Per person').toLowerCase()}`;
}
